import './env-shim.vite.js'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { HelmetProvider } from 'react-helmet-async'
import { DSProvider } from './design-system'
import App from './App'
import './styles/tokens.css'
import './styles/avatar.css'
import './styles/header-search.css'
import './index.css'
import './e2ee/mlsLogGate'
import {
  ensureMessengerV2NotificationServiceWorker,
  installMessengerV2NotificationBridge,
} from './lib/messengerDesktopNotifications'
import store from './redux/store'
import { initSpinDefaultIndicator, withSpinConfig } from './app/spinner-default'
import { initVisitorToken } from './service/request'
import './lib/patch-remove-child'

// CRA-specific polyfills/service worker not included in Vite entry.

const cleanupLegacyAssets = async () => {
  if (typeof navigator !== 'undefined' && navigator.serviceWorker?.getRegistrations) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(
        registrations.map((registration) => {
          const workers = [registration.active, registration.waiting, registration.installing]
          const isMessengerWorker = workers.some((worker) => {
            try {
              return new URL(worker?.scriptURL || '').pathname === '/messenger-notifications-sw.js'
            } catch {
              return false
            }
          })
          return isMessengerWorker ? Promise.resolve(false) : registration.unregister()
        }),
      )
    } catch (error) {
      console.error('Failed to unregister legacy service workers', error)
    }
  }

  if (typeof window !== 'undefined' && 'caches' in window && typeof caches?.keys === 'function') {
    try {
      const cacheKeys = await caches.keys()
      await Promise.all(cacheKeys.map((key) => caches.delete(key)))
    } catch (error) {
      console.error('Failed to clear legacy caches', error)
    }
  }
}

const container = document.getElementById('root')

const startApp = async () => {
  void initVisitorToken().catch(() => null)
  await cleanupLegacyAssets()
  void ensureMessengerV2NotificationServiceWorker().catch(() => null)
  installMessengerV2NotificationBridge({
    onOpenConversation: ({ url, conversationId }) => {
      if (typeof window === 'undefined') {
        return
      }

      const nextUrl = url || (conversationId ? `/messages/${conversationId}` : '/messages')
      if (window.location.pathname + window.location.search === nextUrl) {
        window.focus?.()
        return
      }

      window.location.assign(nextUrl)
    },
  })

  if (!container) {
    throw new Error('Root container element not found')
  }

  const root = createRoot(container)

  initSpinDefaultIndicator()
  // Handle stale asset caches or CDN race: reload on dynamic import failures
  if (typeof window !== 'undefined') {
    const reloadOnChunkError = (reason) => {
      try {
        const msg = String(reason && (reason.message || reason.reason || reason))
        if (
          msg.includes('Failed to fetch dynamically imported module') ||
          msg.includes('Importing a module script failed') ||
          msg.includes('Loading CSS chunk') ||
          msg.includes('ChunkLoadError')
        ) {
          const url = new URL(window.location.href)
          url.searchParams.set('v', String(Date.now()))
          window.location.replace(url.toString())
        }
      } catch (_) {
        window.location.reload()
      }
    }
    window.addEventListener('error', (e) => reloadOnChunkError(e?.message || e?.error))
    window.addEventListener('unhandledrejection', (e) => reloadOnChunkError(e?.reason))
  }
  root.render(
    withSpinConfig(
      <Provider store={store}>
        <DSProvider>
          <HelmetProvider>
            <App />
          </HelmetProvider>
        </DSProvider>
      </Provider>
    )
  )
}

startApp()
